import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield, Lock, Eye, EyeOff, Key, ArrowLeft, CheckCircle,
  AlertCircle, Trash2, Download, ChevronRight, Smartphone
} from "lucide-react";
import { toast } from "sonner";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../lib/firebase";

const Toggle = ({ value, onChange }) => (
  <button
    onClick={() => onChange(!value)}
    className={`relative w-11 h-6 rounded-full transition-colors ${value ? "bg-primary-500" : "bg-slate-200"}`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? "translate-x-5" : "translate-x-0"}`}
    />
  </button>
);

const PrivacySettings = ({ user }) => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    twoFactor:        false,
    analytics:        true,
    personalization:  true,
    dataSharingResearch: false,
  });

  const set = (key, val) => setSettings((p) => ({ ...p, [key]: val }));

  const handlePasswordReset = async () => {
    if (!user?.email) { toast.error("No email associated with your account."); return; }
    try {
      await sendPasswordResetEmail(auth, user.email);
      toast.success(`Password reset email sent to ${user.email}`);
    } catch (err) {
      toast.error(err.message || "Failed to send reset email.");
    }
  };

  const handleExportData = () => {
    toast.info("Data export has been requested. You'll receive an email within 48 hours.");
  };

  const handleDeleteAccount = () => {
    if (window.confirm("This will permanently delete your account. This CANNOT be undone.")) {
      toast.error("Account deletion is not yet available. Please email support@supadoc.in");
    }
  };

  const Section = ({ title, desc, children }) => (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card rounded-2xl overflow-hidden mb-4"
    >
      <div className="px-4 py-3 bg-slate-50/80 border-b border-slate-100">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</p>
        {desc && <p className="text-xs text-slate-400 mt-0.5">{desc}</p>}
      </div>
      {children}
    </motion.div>
  );

  const Row = ({ icon: Icon, label, sub, right, onClick, danger, color = "text-primary-500", bg = "bg-primary-50" }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 last:border-0`}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${bg}`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${danger ? "text-red-500" : "text-slate-800"}`}>{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{sub}</p>}
      </div>
      {right || <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
    </button>
  );

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-blue-50 via-white to-blue-100">
      {/* Header */}
      <div className="glass-panel sticky top-0 z-40 px-4 py-4 border-b border-white/30">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-semibold text-slate-800">Privacy &amp; Security</h1>
            <p className="text-xs text-slate-500">Manage your data &amp; account security</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Security summary card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-5 text-white mb-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold">Security Status</p>
              <p className="text-blue-200 text-xs">Your account</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex items-center gap-1.5 bg-white/10 rounded-xl px-3 py-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-green-300" />
              <span className="text-xs font-medium">Encrypted</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white/10 rounded-xl px-3 py-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-green-300" />
              <span className="text-xs font-medium">Firebase Auth</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white/10 rounded-xl px-3 py-1.5">
              <AlertCircle className="w-3.5 h-3.5 text-amber-300" />
              <span className="text-xs font-medium">2FA Off</span>
            </div>
          </div>
        </motion.div>

        {/* Account Security */}
        <Section title="Account Security" desc="Protect your account from unauthorised access">
          <Row
            icon={Key} label="Change Password"
            sub={user?.email ? `Reset link sent to ${user.email}` : "Send password reset email"}
            onClick={handlePasswordReset}
          />
          <Row
            icon={Smartphone} label="Two-Factor Authentication"
            sub="Add an extra layer of security (coming soon)"
            right={<Toggle value={settings.twoFactor} onChange={() => toast.info("2FA coming soon!")} />}
          />
          <Row
            icon={Eye} label="Active Sessions"
            sub="View devices signed into your account"
            onClick={() => toast.info("Session management coming soon!")}
          />
        </Section>

        {/* Data Privacy */}
        <Section title="Data &amp; Privacy" desc="Control how your data is used">
          <Row
            icon={settings.analytics ? Eye : EyeOff}
            label="Usage Analytics"
            sub="Help us improve SupaDoc with anonymous usage data"
            right={<Toggle value={settings.analytics} onChange={(v) => { set("analytics", v); toast.success(v ? "Analytics enabled" : "Analytics disabled"); }} />}
          />
          <Row
            icon={Shield} label="Personalization"
            sub="Allow AI to remember your health preferences"
            right={<Toggle value={settings.personalization} onChange={(v) => set("personalization", v)} />}
          />
          <Row
            icon={Shield} label="Research Data Sharing"
            sub="Contribute anonymised data to medical research"
            right={<Toggle value={settings.dataSharingResearch} onChange={(v) => set("dataSharingResearch", v)} />}
          />
        </Section>

        {/* Data Management */}
        <Section title="Your Data">
          <Row
            icon={Download} label="Export My Data"
            sub="Download a copy of all your SupaDoc data"
            onClick={handleExportData}
          />
          <Row
            icon={Trash2} label="Delete All Chat History"
            sub="Permanently remove all AI conversations"
            onClick={() => toast.info("Chat history deletion coming soon!")}
            danger color="text-red-500" bg="bg-red-50"
          />
          <Row
            icon={Trash2} label="Delete Account"
            sub="Permanently delete your account and all data"
            onClick={handleDeleteAccount}
            danger color="text-red-500" bg="bg-red-50"
          />
        </Section>

        {/* Legal */}
        <Section title="Legal">
          <Row icon={Shield} label="Privacy Policy" onClick={() => window.open("https://supadoc.in/privacy", "_blank")} />
          <Row icon={Shield} label="Terms of Service" onClick={() => window.open("https://supadoc.in/terms", "_blank")} />
          <Row icon={Shield} label="Cookie Policy" onClick={() => window.open("https://supadoc.in/cookies", "_blank")} />
        </Section>

        <p className="text-center text-xs text-slate-400 mt-2">
          Your health data is encrypted end-to-end and never sold to third parties.
        </p>
      </div>
    </div>
  );
};

export default PrivacySettings;
